import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useConfirmations } from '../../hooks/useConfirmations';
import { useWhatsAppTemplates } from '../../hooks/useWhatsAppTemplates';
import { useSalon } from '../../contexts/SalonContext';
import { useProfessionals } from '../../contexts/ProfessionalContext';
import { formatDate } from '../../utils/dateUtils';
import { formatWhatsApp } from '../../utils/formatUtils';
import { Alert, Button, StatusBadge } from '../ui';
import AppointmentDetailModal from './AppointmentDetailModal';
import { ensureAppointmentHasToken } from '../../firebase/appointments';
import PullToRefresh from '../ui/PullToRefresh';

/**
 * Componente da página de Confirmações
 * Exibe agendamentos pendentes de confirmação e permite confirmar ou enviar mensagem WhatsApp
 * Agora com agrupamento por data (Hoje, Amanhã, Próximos dias) e templates de mensagens
 * 
 * @returns {JSX.Element} Componente React
 */
const ConfirmationsPage = () => {
  const { salonId, salonConfig } = useSalon();
  const { professionals } = useProfessionals();
  const { 
    pendingConfirmations, 
    messageStatus,
    loading, 
    error, 
    confirmAppointment, 
    sendWhatsAppMessage,
    isMessageSent,
    refreshConfirmations: originalRefreshConfirmations,
    updateAppointment,
    cancelAppointment
  } = useConfirmations(salonId);
  
  // Hook para gerenciar templates de WhatsApp
  const {
    templates,
    loading: templatesLoading,
    processTemplate,
    getDefaultTemplateForType
  } = useWhatsAppTemplates(salonId);

  // Estado para acompanhar a aba ativa
  const [activeTab, setActiveTab] = useState('today');
  
  // Estado para filtro de mensagens
  const [messageFilter, setMessageFilter] = useState('all'); // 'all', 'sent', 'pending'
  
  // Estado para exibir mensagem de feedback
  const [feedbackMessage, setFeedbackMessage] = useState(null);
  
  // Estado para armazenar os agendamentos agrupados por data
  const [groupedConfirmations, setGroupedConfirmations] = useState({
    today: [],
    tomorrow: [],
    upcoming: []
  });

  // Estado para controlar o modal de detalhes do agendamento
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Ref para o container principal
  const containerRef = useRef(null);
  
  // Estado para controlar a dica de pull-to-refresh
  const [showPullHint, setShowPullHint] = useState(false);
  
  // Estado para rastrear IDs de agendamentos antes da atualização
  const [prevAppointmentIds, setPrevAppointmentIds] = useState([]);
  
  // Estado para armazenar IDs de agendamentos novos ou atualizados
  const [updatedAppointmentIds, setUpdatedAppointmentIds] = useState([]);
  
  // Configurar comportamento de scroll para dispositivos móveis
  useEffect(() => {
    // Verificar se estamos em um dispositivo móvel
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
    
    if (isMobile && containerRef.current) {
      // Impedir scroll elástico em iOS que pode interferir com pull-to-refresh
      document.body.style.overflow = 'hidden';
      
      // Garantir que o overflow-y do container permita scroll
      containerRef.current.style.overflowY = 'auto';
      containerRef.current.style.height = '100vh';
      containerRef.current.style.WebkitOverflowScrolling = 'touch';
      
      // Limpar ao desmontar
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, []);
  
  // Efeito para mostrar a dica de pull-to-refresh apenas na primeira visita
  useEffect(() => {
    // Verificar se o usuário já viu a dica antes
    const hasSeenPullHint = localStorage.getItem('hasSeenPullHint');
    
    // Verificar se estamos em um dispositivo móvel
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
    
    if (isMobile && !hasSeenPullHint) {
      // Mostrar a dica após um breve delay
      setTimeout(() => {
        setShowPullHint(true);
        
        // Ocultar após 5 segundos
        setTimeout(() => {
          setShowPullHint(false);
          // Marcar como vista
          localStorage.setItem('hasSeenPullHint', 'true');
        }, 5000);
      }, 1500);
    }
  }, []);
  
  // Funções para manipular o modal
  const openModal = (appointment) => {
    setSelectedAppointment(appointment);
    setIsModalOpen(true);
  };
  
  const closeModal = () => {
    setIsModalOpen(false);
  };
  
  // Funções para ações do modal
  const handleReschedule = async (appointmentId, updateData) => {
    try {
      // Usar o método do contexto para atualizar o agendamento
      const success = await updateAppointment(appointmentId, updateData);
      
      if (success) {
        setFeedbackMessage({
          type: 'success',
          message: 'Agendamento remarcado com sucesso!'
        });
      } else {
        throw new Error('Não foi possível remarcar o agendamento');
      }
      
      closeModal();
      setTimeout(() => setFeedbackMessage(null), 3000);
    } catch (error) {
      console.error('Erro ao remarcar agendamento:', error);
      setFeedbackMessage({
        type: 'error',
        message: 'Erro ao remarcar agendamento. Tente novamente.'
      });
      setTimeout(() => setFeedbackMessage(null), 3000);
    }
  };
  
  const handleCancelAppointment = async (appointmentId) => {
    try {
      // Usar o método do contexto para cancelar o agendamento
      const success = await cancelAppointment(appointmentId);
      
      if (success) {
        setFeedbackMessage({
          type: 'success',
          message: 'Agendamento cancelado com sucesso!'
        });
      } else {
        throw new Error('Não foi possível cancelar o agendamento');
      }
      
      closeModal();
      setTimeout(() => setFeedbackMessage(null), 3000);
    } catch (error) {
      console.error('Erro ao cancelar agendamento:', error);
      setFeedbackMessage({
        type: 'error',
        message: 'Erro ao cancelar agendamento. Tente novamente.'
      });
      setTimeout(() => setFeedbackMessage(null), 3000);
    }
  };
  
  const handleEditServices = async (appointmentId, updateData) => {
    try {
      // Usar o método do contexto para atualizar os serviços do agendamento
      const success = await updateAppointment(appointmentId, updateData);
      
      if (success) {
        setFeedbackMessage({
          type: 'success',
          message: 'Serviços atualizados com sucesso!'
        });
      } else {
        throw new Error('Não foi possível atualizar os serviços');
      }
      
      closeModal();
      setTimeout(() => setFeedbackMessage(null), 3000);
    } catch (error) {
      console.error('Erro ao editar serviços:', error);
      setFeedbackMessage({
        type: 'error',
        message: 'Erro ao atualizar serviços. Tente novamente.'
      });
      setTimeout(() => setFeedbackMessage(null), 3000);
    }
  };
  
  const handleMarkAsPaid = async (appointmentId, updateData = {}) => {
    try {
      // Adicionar log para debug
      console.log('ConfirmationsPage - Marcando como pago:', appointmentId, updateData);
      
      // Usar o método do contexto para marcar como pago
      const dataToUpdate = {
        pago: true,
        ...updateData
      };
      
      // Verificar se há valorCustomizado e se ele é 0 ou null
      if (dataToUpdate.valorCustomizado === 0 || dataToUpdate.valorCustomizado === null) {
        // Remover o campo para não sobrescrever um valor existente com 0 ou null
        console.log('Removendo valorCustomizado nulo ou zero');
        delete dataToUpdate.valorCustomizado;
      }
      
      console.log('Dados finais para atualização:', dataToUpdate);
      
      const success = await updateAppointment(appointmentId, dataToUpdate);
      
      if (success) {
        setFeedbackMessage({
          type: 'success',
          message: 'Agendamento marcado como pago com sucesso!'
        });
      } else {
        throw new Error('Não foi possível marcar como pago');
      }
      
      closeModal();
      setTimeout(() => setFeedbackMessage(null), 3000);
    } catch (error) {
      console.error('Erro ao marcar como pago:', error);
      setFeedbackMessage({
        type: 'error',
        message: 'Erro ao marcar como pago. Tente novamente.'
      });
      setTimeout(() => setFeedbackMessage(null), 3000);
    }
  };

  // Quando as confirmações pendentes forem carregadas, agrupe-as por data
  useEffect(() => {
    if (!pendingConfirmations || pendingConfirmations.length === 0) {
      setGroupedConfirmations({
        today: [],
        tomorrow: [],
        upcoming: []
      });
      return;
    }

    // Obter datas para hoje e amanhã
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    // Função para comparar datas (ignorando horário)
    const isSameDay = (date1, date2) => {
      return date1.getFullYear() === date2.getFullYear() &&
             date1.getMonth() === date2.getMonth() &&
             date1.getDate() === date2.getDate();
    };

    // Agrupar agendamentos
    const grouped = {
      today: [],
      tomorrow: [],
      upcoming: []
    };

    pendingConfirmations.forEach(appointment => {
      // Converter a data do agendamento para objeto Date
      let appointmentDate;
      
      try {
        if (appointment.data) {
          // Se for formato ISO (YYYY-MM-DD)
          appointmentDate = new Date(appointment.data + 'T00:00:00');
        } else if (appointment.date) {
          // Se estiver usando outra propriedade
          appointmentDate = new Date(appointment.date + 'T00:00:00');
        }

        // Verificar se a data é válida
        if (isNaN(appointmentDate.getTime())) {
          console.error("Data inválida no agendamento:", appointment);
          return; // Pular este agendamento
        }

        // Determinar categoria
        if (isSameDay(appointmentDate, today)) {
          grouped.today.push(appointment);
        } else if (isSameDay(appointmentDate, tomorrow)) {
          grouped.tomorrow.push(appointment);
        } else if (appointmentDate > today) {
          grouped.upcoming.push(appointment);
        }
      } catch (error) {
        console.error("Erro ao processar data do agendamento:", error, appointment);
      }
    });

    // Função para ordenar agendamentos: primeiro os sem mensagem, depois por horário
    const sortAppointments = (appointments) => {
      return appointments.sort((a, b) => {
        const aMessageSent = a.mensagemEnviada || messageStatus[a.id] || false;
        const bMessageSent = b.mensagemEnviada || messageStatus[b.id] || false;

        // Primeiro critério: mensagem não enviada tem prioridade
        if (aMessageSent !== bMessageSent) {
          return aMessageSent ? 1 : -1;
        }
        
        // Segundo critério: ordenar por horário
        const timeA = a.horario || a.time || '';
        const timeB = b.horario || b.time || '';
        return timeA.localeCompare(timeB);
      });
    };

    // Ordenar os agendamentos em cada grupo
    grouped.today = sortAppointments(grouped.today);
    grouped.tomorrow = sortAppointments(grouped.tomorrow);
    grouped.upcoming = sortAppointments(grouped.upcoming);

    setGroupedConfirmations(grouped);

    // Depois de agrupar, verifique itens novos/atualizados
    const currentIds = pendingConfirmations.map(a => a.id);
    
    // Encontre quais IDs são novos (não estavam na lista anterior)
    if (prevAppointmentIds.length > 0) {
      const newIds = currentIds.filter(id => !prevAppointmentIds.includes(id));
      if (newIds.length > 0) {
        setUpdatedAppointmentIds(newIds);
        
        // Remover os destaques após 2 segundos
        setTimeout(() => {
          setUpdatedAppointmentIds([]);
        }, 2000);
      }
    }
    
    // Armazene os IDs atuais para a próxima comparação
    setPrevAppointmentIds(currentIds);
  }, [pendingConfirmations, messageStatus]);

  const handleConfirm = async (appointmentId) => {
    const success = await confirmAppointment(appointmentId);
    if (success) {
      setFeedbackMessage({
        type: 'success',
        message: 'Agendamento confirmado com sucesso!'
      });
      
      // Limpar mensagem após 3 segundos
      setTimeout(() => setFeedbackMessage(null), 3000);
    } else {
      setFeedbackMessage({
        type: 'error',
        message: 'Erro ao confirmar agendamento. Por favor, tente novamente.'
      });
    }
  };

  /**
   * Função utilitária para resolver o nome do profissional
   * @param {string|object} professionalData - ID do profissional ou objeto com dados do profissional
   * @returns {string} Nome do profissional ou mensagem padrão
   */
  const resolveProfessionalName = (professionalData) => {
    // Se não houver dados, retornar mensagem padrão
    if (!professionalData) return 'Profissional não especificado';

    // Se for um objeto com propriedade nome, usar diretamente
    if (typeof professionalData === 'object' && professionalData.nome) {
      return professionalData.nome;
    }

    // Se for string (ID) e temos a lista de profissionais, buscar pelo ID
    if (typeof professionalData === 'string' && professionals && professionals.length > 0) {
      const foundProfessional = professionals.find(p => p.id === professionalData);
      if (foundProfessional) return foundProfessional.nome;
    }

    // Buscar no campo profissionalNome se disponível
    if (typeof professionalData === 'object' && professionalData.profissionalNome) {
      return professionalData.profissionalNome;
    }

    // Fallback para casos inesperados
    return typeof professionalData === 'string' ? professionalData : 'Profissional';
  };

  const handleSendWhatsApp = async (appointment) => {
    try {
      // Garantir que o agendamento tem token de acesso
      let appointmentWithToken = appointment;
      
      // Se não tiver token ou ele estiver expirado, gerar um novo
      if (!appointment.accessToken) {
        try {
          appointmentWithToken = await ensureAppointmentHasToken(appointment.id);
          console.log('Token gerado para agendamento:', appointment.id);
        } catch (err) {
          console.error('Erro ao gerar token para agendamento:', err);
          setFeedbackMessage({
            type: 'error',
            message: 'Erro ao gerar links para o cliente. Por favor, tente novamente.'
          });
          return false;
        }
      }
      
      // Obter o template para confirmação
      let message = '';
      
      // Obter o template para o tipo 'confirmacao'
      const template = getDefaultTemplateForType('confirmacao');
      
      if (template) {
        // Preparar os dados para substituir as variáveis
        const serviceNames = Array.isArray(appointment.servicos) 
          ? appointment.servicos
              .map(s => typeof s === 'object' && s.nome ? s.nome : 'Serviço')
              .join(', ')
          : 'Serviço';
          
        // Usar a função utilitária para resolver o nome do profissional
        const profissionalName = resolveProfessionalName(appointment.profissional);
          
        const appointmentData = {
          cliente: appointment.cliente || 'Cliente',
          data: formatDate(appointment.data),
          hora: (appointment.horario || '').substring(0, 5),
          servico: serviceNames,
          profissional: profissionalName,
          valor: appointment.valorTotal ? `R$ ${appointment.valorTotal.toFixed(2)}` : '',
          salao: salonConfig?.nome || 'Salão',
          telefone: appointment.whatsapp || '',
          email: appointment.email || '',
          // Adicionar ID e token para gerar links
          id: appointment.id,
          accessToken: appointmentWithToken.accessToken
        };
        
        // Processar o template com os links dinâmicos
        message = processTemplate(template.message, appointmentData);
      }
      
      // Enviar a mensagem
      const success = await sendWhatsAppMessage(appointmentWithToken, message);
      
      if (success) {
        setFeedbackMessage({
          type: 'success',
          message: 'Mensagem enviada! O cliente agora pode confirmar, reagendar ou cancelar pelo link.',
          highlight: true
        });
        
        // Limpar mensagem após 5 segundos
        setTimeout(() => setFeedbackMessage(null), 5000);
        return true;
      } else {
        setFeedbackMessage({
          type: 'error',
          message: 'Erro ao enviar mensagem WhatsApp. Por favor, tente novamente.'
        });
        return false;
      }
    } catch (err) {
      console.error('Erro ao processar mensagem:', err);
      setFeedbackMessage({
        type: 'error',
        message: 'Erro ao processar mensagem. Por favor, tente novamente.'
      });
      return false;
    }
  };

  // Obter a contagem de cada categoria para exibir nas abas
  const todayCount = groupedConfirmations.today.length;
  const tomorrowCount = groupedConfirmations.tomorrow.length;
  const upcomingCount = groupedConfirmations.upcoming.length;

  // Obter o título da seção baseado na aba ativa
  const getTabTitle = () => {
    switch (activeTab) {
      case 'today':
        return 'Confirmações Para Hoje';
      case 'tomorrow':
        return 'Confirmações Para Amanhã';
      case 'upcoming':
        return 'Confirmações Futuras';
      default:
        return 'Confirmações Pendentes';
    }
  };

  // Filtrar agendamentos de acordo com o filtro selecionado
  const filterAppointmentsByMessageStatus = (appointments) => {
    if (messageFilter === 'all') return appointments;
    
    return appointments.filter(appointment => {
      const messageSent = appointment.mensagemEnviada || messageStatus[appointment.id] || false;
      return messageFilter === 'sent' ? messageSent : !messageSent;
    });
  };

  // Dividir agendamentos em duas seções: com e sem mensagem enviada
  const groupAppointmentsByMessageStatus = (appointments) => {
    const withoutMessage = [];
    const withMessage = [];
    
    appointments.forEach(appointment => {
      const hasSentMessage = appointment.mensagemEnviada || messageStatus[appointment.id] || false;
      
      if (hasSentMessage) {
        withMessage.push(appointment);
      } else {
        withoutMessage.push(appointment);
      }
    });
    
    return { withoutMessage, withMessage };
  };

  // Renderiza um agendamento individual
  const renderAppointmentItem = (appointment) => {
    const messageSent = isMessageSent(appointment.id);
    
    // Preparar o valor formatado
    const formattedValue = appointment.valorTotal 
      ? `R$ ${Number(appointment.valorTotal).toFixed(2).replace('.', ',')}`
      : '';
    
    // Verificar se este item é novo/atualizado
    const isUpdated = updatedAppointmentIds.includes(appointment.id);
    
    // Processar informações do profissional usando a função utilitária
    const professionalName = resolveProfessionalName(appointment.profissional);
    
    // Processar informações de serviços de forma mais robusta
    const servicesText = Array.isArray(appointment.servicos) 
      ? appointment.servicos.map(service => {
          if (typeof service === 'object' && service.nome) {
            return service.nome;
          } else if (Array.isArray(appointment.servicosDetalhes)) {
            // Tentar encontrar detalhes do serviço se disponível
            const serviceDetail = appointment.servicosDetalhes.find(s => s.id === service);
            return serviceDetail?.nome || 'Serviço';
          }
          return 'Serviço';
        }).join(', ')
      : 'Serviço não especificado';
    
    // Determinar o status
    let status = 'pendente';
    let statusColor = 'bg-yellow-100 text-yellow-800';
    
    if (appointment.cancelado) {
      status = 'cancelado';
      statusColor = 'bg-red-100 text-red-800';
    } else if (appointment.confirmado) {
      status = 'confirmado';
      statusColor = 'bg-green-100 text-green-800';
    } else if (messageSent) {
      status = 'msg enviada';
      statusColor = 'bg-blue-100 text-blue-800';
    }
    
    return (
      <li 
        key={appointment.id} 
        className={`p-4 hover:bg-gray-50 ${messageSent ? 'opacity-90' : ''} ${isUpdated ? 'highlight-updated' : ''} cursor-pointer`}
        onClick={() => openModal(appointment)}
      >
        <div className="flex flex-col sm:flex-row sm:justify-between">
          <div className="relative w-full">
            {/* Valor no canto superior direito */}
            {formattedValue && (
              <div className="absolute top-0 right-0 sm:right-4 bg-blue-50 text-blue-800 px-2 py-1 rounded text-sm font-medium">
                {formattedValue}
              </div>
            )}
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-medium">{appointment.cliente || 'Cliente'}</h3>
              
              {/* Badge de status */}
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statusColor}`}>
                {status}
              </span>
              
              {messageSent && (
                <span className="flex items-center text-green-600">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-xs text-green-600 hidden sm:inline ml-1">Mensagem enviada</span>
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500">
              {formatDate(appointment.data)} às {appointment.horario?.substring(0, 5) || 'horário não definido'}
            </p>
            <p className="text-sm">
              {servicesText}
            </p>
            {appointment.whatsapp && (
              <p className="text-sm text-gray-500 flex items-center">
                WhatsApp: {formatWhatsApp(appointment.whatsapp)}
                {messageSent && (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1 text-green-600" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </p>
            )}
            <p className="text-sm text-gray-500">
              Profissional: {professionalName}
            </p>
          </div>
          <div className="flex flex-col gap-2 mt-4 sm:mt-0">
            {/* Botões de ação - empilhados no mobile, lado a lado em desktop */}
            <div className="flex flex-col sm:flex-row gap-2 sm:space-x-2">
              <Button 
                onClick={(e) => {
                  e.stopPropagation(); // Impedir que o clique abra o modal
                  handleConfirm(appointment.id);
                }}
                disabled={loading || appointment.confirmado || appointment.cancelado}
                variant="primary"
                size="sm"
                fullWidth={false}
                className="w-full sm:w-auto"
              >
                Confirmar
              </Button>
              <Button 
                onClick={(e) => {
                  e.stopPropagation(); // Impedir que o clique abra o modal
                  handleSendWhatsApp(appointment);
                }}
                disabled={loading || templatesLoading || appointment.cancelado}
                variant={messageSent ? "outline" : "secondary"}
                size="sm"
                fullWidth={false}
                className={`flex items-center justify-center gap-1 w-full sm:w-auto`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24" className="w-4 h-4">
                  <path d="M12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21 5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.816 9.816 0 0012.04 2m.01 1.67c2.2 0 4.26.86 5.82 2.42a8.225 8.225 0 012.41 5.83c0 4.54-3.7 8.23-8.24 8.23-1.48 0-2.93-.39-4.19-1.15l-.3-.17-3.12.82.83-3.04-.2-.32a8.188 8.188 0 01-1.26-4.38c.01-4.54 3.7-8.24 8.25-8.24M8.53 7.33c-.16 0-.43.06-.66.31-.22.25-.87.86-.87 2.07 0 1.22.89 2.39 1 2.56.14.17 1.76 2.67 4.25 3.73.59.27 1.05.42 1.41.53.59.19 1.13.16 1.56.1.48-.07 1.46-.6 1.67-1.18.21-.58.21-1.07.15-1.18-.07-.1-.23-.16-.48-.27-.25-.14-1.47-.74-1.69-.82-.23-.08-.37-.12-.56.12-.16.25-.64.81-.78.97-.15.17-.29.19-.53.07-.26-.13-1.06-.39-2-1.23-.74-.66-1.23-1.47-1.38-1.72-.12-.24-.01-.39.11-.5.11-.11.27-.29.37-.44.13-.14.17-.25.25-.41.08-.17.04-.31-.02-.43-.06-.11-.56-1.35-.77-1.84-.2-.48-.4-.42-.56-.43-.14 0-.3-.01-.47-.01z" />
                </svg>
                {messageSent ? 'Reenviar' : 'WhatsApp'}
              </Button>
            </div>
          </div>
        </div>
      </li>
    );
  };

  // Renderiza a lista de agendamentos para a aba selecionada
  const renderAppointmentsList = () => {
    const appointments = filterAppointmentsByMessageStatus(groupedConfirmations[activeTab] || []);
    
    if (appointments.length === 0) {
      return (
        <div className="p-6 text-center">
          <p className="text-gray-500">Não há confirmações pendentes para este período.</p>
        </div>
      );
    }

    // Agrupar agendamentos por status de mensagem
    const { withoutMessage, withMessage } = groupAppointmentsByMessageStatus(appointments);

    return (
      <div>
        {withoutMessage.length > 0 && (
          <div className="mb-6">
            <h4 className="text-sm uppercase font-medium text-gray-500 mb-2 px-4">Aguardando envio de mensagem</h4>
            <ul className="divide-y divide-gray-200">
              {withoutMessage.map(renderAppointmentItem)}
            </ul>
          </div>
        )}
        
        {withMessage.length > 0 && (
          <div>
            <h4 className="text-sm uppercase font-medium text-gray-500 mb-2 px-4">Mensagem enviada - aguardando confirmação</h4>
            <ul className="divide-y divide-gray-200">
              {withMessage.map(renderAppointmentItem)}
            </ul>
          </div>
        )}
      </div>
    );
  };

  // Adaptar refreshConfirmations para retornar uma Promise como esperado pelo PullToRefresh
  const handleRefresh = useCallback(() => {
    // Armazenar os IDs atuais antes da atualização para comparação posterior
    const currentIds = pendingConfirmations.map(a => a.id);
    setPrevAppointmentIds(currentIds);
    
    return new Promise((resolve) => {
      // Chamar a função original e resolver a promise quando concluir
      originalRefreshConfirmations();
      
      // Dar um pequeno atraso para melhorar a experiência do usuário
      setTimeout(() => {
        resolve();
      }, 800);
    });
  }, [originalRefreshConfirmations, pendingConfirmations]);

  if (error) {
    return <Alert type="error" title="Erro ao carregar confirmações">
      {error}
    </Alert>;
  }

  // Total de confirmações pendentes para mostrar no título
  const totalPending = todayCount + tomorrowCount + upcomingCount;

  return (
    <div className="space-y-6" ref={containerRef}>
      {/* Mensagem de feedback */}
      {feedbackMessage && (
        <div className={`rounded-md p-4 ${
          feedbackMessage.type === 'success' 
            ? feedbackMessage.highlight 
              ? 'bg-green-100 border-green-500 border-2' 
              : 'bg-green-50 border border-green-200' 
            : 'bg-red-50 border border-red-200'
        }`}>
          <div className="flex items-center">
            <div className={`flex-shrink-0 ${
              feedbackMessage.type === 'success' ? 'text-green-600' : 'text-red-600'
            }`}>
              {feedbackMessage.type === 'success' ? (
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              )}
            </div>
            <div className="ml-3">
              <p className={`text-sm font-medium ${
                feedbackMessage.type === 'success' ? 'text-green-800' : 'text-red-800'
              }`}>
                {feedbackMessage.message}
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Dica de pull-to-refresh */}
      {showPullHint && (
        <div className="fixed top-1/4 left-0 right-0 mx-auto w-64 bg-blue-500 text-white py-2 px-4 rounded-full text-center shadow-lg z-50 animate-bounce">
          <div className="flex items-center justify-center">
            <svg className="h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
            <span>Puxe para atualizar</span>
          </div>
        </div>
      )}

      {/* Navegação por abas */}
      <div className="border-b border-gray-200">
        <div className="flex space-x-4">
          <button
            onClick={() => setActiveTab('today')}
            className={`py-2 px-4 border-b-2 font-medium text-sm ${
              activeTab === 'today'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Hoje ({todayCount})
          </button>
          <button
            onClick={() => setActiveTab('tomorrow')}
            className={`py-2 px-4 border-b-2 font-medium text-sm ${
              activeTab === 'tomorrow'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Amanhã ({tomorrowCount})
          </button>
          <button
            onClick={() => setActiveTab('upcoming')}
            className={`py-2 px-4 border-b-2 font-medium text-sm ${
              activeTab === 'upcoming'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Próximos Dias ({upcomingCount})
          </button>
        </div>
      </div>
      
      {/* Título da seção atual */}
      <h3 className="text-lg font-medium text-gray-700">{getTabTitle()}</h3>
      
      {/* Conteúdo principal com pull-to-refresh */}
      <PullToRefresh
        onRefresh={handleRefresh}
        pullDownThreshold={80}
        resistance={2.5}
        className="bg-transparent"
        distThreshold={2}
        distMax={3}
        distReload={3}
        pullingContent={
          <div className="flex justify-center items-center p-3 text-blue-500">
            <svg className="animate-pulse h-6 w-6 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            </svg>
            <span>Puxe para atualizar</span>
          </div>
        }
        refreshingContent={
          <div className="flex justify-center items-center p-3 text-blue-500">
            <svg className="animate-spin h-6 w-6 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span>Atualizando...</span>
          </div>
        }>
        <div className="bg-white rounded-lg shadow overflow-hidden relative">
          {loading || templatesLoading ? (
            <div className="p-6 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
              <p className="mt-2 text-gray-500">Carregando...</p>
            </div>
          ) : (
            renderAppointmentsList()
          )}
        </div>
      </PullToRefresh>

      {/* Modal de detalhes do agendamento */}
      <AppointmentDetailModal
        appointment={selectedAppointment}
        isOpen={isModalOpen}
        onClose={closeModal}
        onReschedule={handleReschedule}
        onCancel={handleCancelAppointment}
        onEditServices={handleEditServices}
        onMarkAsPaid={handleMarkAsPaid}
      />
    </div>
  );
};

export default ConfirmationsPage; 